// Semicolon normalization helpers for inline and standalone var calls.
// Example: "var(--x) color:red" -> "var(--x); color:red".
import { parseVarCallAt, isStandaloneVarCall } from './var-call'
import { skipWhitespace } from './chars'
import { splitLines } from './lines'
import { startsDeclarationAt } from './declarations'

// Normalize `var(...)` semicolon patterns line-by-line.
export function normalizeVarSemicolons(source: string): string {
  const lines = splitLines(source)
  const normalizedLines: string[] = []

  // Apply inline and standalone var-call fixes in deterministic order.
  for (const rawLine of lines) {
    const withInlineSemicolons = insertMissingSemicolonBeforeDeclaration(rawLine)
    const withStandaloneSemicolon = ensureStandaloneVarCallSemicolon(withInlineSemicolons)
    normalizedLines.push(withStandaloneSemicolon)
  }

  // Return source text with normalized var-call semicolon usage.
  return normalizedLines.join('\n')
}

// Insert `;` after `var(...)` when next token on same line is a declaration.
export function insertMissingSemicolonBeforeDeclaration(line: string): string {
  let output = ''
  let index = 0

  // Walk one line and rewrite only valid `var(...)` call segments.
  while (index < line.length) {
    const varCall = parseVarCallAt(line, index)
    // Keep raw character when current position is not a var-call start.
    if (!varCall) {
      output += line[index]
      index += 1
      continue
    } else {
      // Continue semicolon insertion checks for parsed var-call.
    }

    output += varCall.raw
    const afterCallIndex = varCall.end
    const afterWhitespaceIndex = skipWhitespace(line, afterCallIndex)
    const nextChar = line[afterWhitespaceIndex] ?? ''

    // Insert missing separator before property declaration on same line.
    if (nextChar !== ';' && startsDeclarationAt(line, afterWhitespaceIndex)) {
      output += ';'
    } else {
      // Keep original text when semicolon is already present.
    }

    index = afterCallIndex
  }

  // Return line with inserted inline semicolons where needed.
  return output
}

// Ensure standalone `var(...)` lines end with semicolon.
export function ensureStandaloneVarCallSemicolon(line: string): string {
  const trimmed = line.trim()
  // Keep non-standalone lines unchanged.
  if (!isStandaloneVarCall(trimmed)) {
    return line
  } else {
    // Continue standalone var-call semicolon checks.
  }

  // Keep already-terminated standalone var calls unchanged.
  if (trimmed.endsWith(';')) {
    return line
  } else {
    // Return var-call line with appended semicolon.
    return `${line.trimEnd()};`
  }
}

// Check if line contains `var(...)` followed by a declaration without `;`.
export function hasInlineVarMissingSemicolon(line: string): boolean {
  let index = 0

  // Walk line and detect var-call + declaration transitions.
  while (index < line.length) {
    const varCall = parseVarCallAt(line, index)
    // Continue scan when current index is not var-call start.
    if (!varCall) {
      index += 1
      continue
    } else {
      // Continue inline semicolon verification.
    }

    const afterWhitespaceIndex = skipWhitespace(line, varCall.end)
    const nextChar = line[afterWhitespaceIndex] ?? ''
    // Return true when declaration starts before a semicolon.
    if (nextChar !== ';' && startsDeclarationAt(line, afterWhitespaceIndex)) {
      return true
    } else {
      // Continue scanning next var-call.
    }

    index = varCall.end
  }

  // Return false when no missing-inline-semicolon pattern is found.
  return false
}
